import React, { useCallback, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Chip,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { alpha } from "@mui/material/styles";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import MergeRoundedIcon from "@mui/icons-material/MergeRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import api from "services/api";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import { useWebSocket } from "contexts/WebSocketContext";

const LinguistMergeOverlay = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const { overlayData, setOverlayName, triggerPageRefresh, showSnackbar } =
    useStateContext();
  const { currentUser } = useAuth();
  const { socket } = useWebSocket();

  const linguists = useMemo(() => {
    return Array.isArray(overlayData?.Linguists) ? overlayData.Linguists : [];
  }, [overlayData?.Linguists]);

  const linguistById = useMemo(() => {
    return new Map(linguists.map((linguist) => [Number(linguist.id), linguist]));
  }, [linguists]);

  const selectedIds = useMemo(() => {
    const raw = overlayData?.SelectedLinguistIds || [];
    return Array.isArray(raw) ? raw.map(Number) : [];
  }, [overlayData?.SelectedLinguistIds]);

  const normalizeName = useCallback((value) => {
    return String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }, []);

  const damerauLevenshteinDistance = useCallback((a, b) => {
    const left = String(a || "");
    const right = String(b || "");

    const leftLength = left.length;
    const rightLength = right.length;

    if (leftLength === 0) return rightLength;
    if (rightLength === 0) return leftLength;

    const matrix = Array.from({ length: leftLength + 1 }, () =>
      new Array(rightLength + 1).fill(0),
    );

    for (let i = 0; i <= leftLength; i += 1) {
      matrix[i][0] = i;
    }

    for (let j = 0; j <= rightLength; j += 1) {
      matrix[0][j] = j;
    }

    for (let i = 1; i <= leftLength; i += 1) {
      for (let j = 1; j <= rightLength; j += 1) {
        const substitutionCost = left[i - 1] === right[j - 1] ? 0 : 1;

        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + substitutionCost,
        );

        if (
          i > 1 &&
          j > 1 &&
          left[i - 1] === right[j - 2] &&
          left[i - 2] === right[j - 1]
        ) {
          matrix[i][j] = Math.min(matrix[i][j], matrix[i - 2][j - 2] + 1);
        }
      }
    }

    return matrix[leftLength][rightLength];
  }, []);

  const normalizedSimilarity = useCallback(
    (left, right) => {
      const a = String(left || "");
      const b = String(right || "");
      const maxLength = Math.max(a.length, b.length);

      if (!maxLength) return 1;

      const distance = damerauLevenshteinDistance(a, b);
      return Math.max(0, 1 - distance / maxLength);
    },
    [damerauLevenshteinDistance],
  );

  const getNameMatchPercent = useCallback(
    (baseName, candidateName) => {
      const base = normalizeName(baseName);
      const candidate = normalizeName(candidateName);

      if (!base || !candidate) return 0;
      if (base === candidate) return 100;

      const compactBase = base.replace(/\s+/g, "");
      const compactCandidate = candidate.replace(/\s+/g, "");
      const fullNameScore = normalizedSimilarity(compactBase, compactCandidate);

      const baseTokens = base.split(" ").filter(Boolean);
      const candidateTokens = candidate.split(" ").filter(Boolean);

      const tokenLengthsTotal = baseTokens.reduce(
        (sum, token) => sum + token.length,
        0,
      );

      const tokenScore =
        tokenLengthsTotal > 0
          ? baseTokens.reduce((sum, token) => {
              const bestTokenSimilarity = candidateTokens.reduce(
                (best, otherToken) =>
                  Math.max(best, normalizedSimilarity(token, otherToken)),
                0,
              );
              return sum + bestTokenSimilarity * token.length;
            }, 0) / tokenLengthsTotal
          : 0;

      const firstNameScore = normalizedSimilarity(
        baseTokens[0] || "",
        candidateTokens[0] || "",
      );
      const lastNameScore = normalizedSimilarity(
        baseTokens[baseTokens.length - 1] || "",
        candidateTokens[candidateTokens.length - 1] || "",
      );

      const finalScore =
        fullNameScore * 0.55 +
        tokenScore * 0.2 +
        firstNameScore * 0.1 +
        lastNameScore * 0.15;

      return Math.max(0, Math.min(100, Math.round(finalScore * 100)));
    },
    [normalizeName, normalizedSimilarity],
  );

  const initialMergeIds = useMemo(() => {
    return selectedIds.length ? selectedIds : [];
  }, [selectedIds]);

  const initialOtherIds = useMemo(() => {
    if (initialMergeIds.length === 0) return [];

    const mergeSet = new Set(initialMergeIds);
    const keepLinguistId = Number(initialMergeIds[0]);
    const keepLinguist = linguistById.get(keepLinguistId);
    const keepName = keepLinguist?.Name || "";
    const keepShortName = keepLinguist?.ShortName || "";

    return linguists
      .map((linguist) => Number(linguist.id))
      .filter((id) => !mergeSet.has(id))
      .map((id, index) => {
        const linguist = linguistById.get(id);
        return {
          id,
          index,
          nameScore: getNameMatchPercent(keepName, linguist?.Name),
          shortNameScore: getNameMatchPercent(
            keepShortName,
            linguist?.ShortName || "",
          ),
        };
      })
      .sort((a, b) => {
        if (b.nameScore !== a.nameScore) return b.nameScore - a.nameScore;
        if (b.shortNameScore !== a.shortNameScore) {
          return b.shortNameScore - a.shortNameScore;
        }
        return a.index - b.index;
      })
      .map((item) => item.id);
  }, [getNameMatchPercent, initialMergeIds, linguistById, linguists]);

  const [linguistsToMergeIds, setLinguistsToMergeIds] =
    useState(initialMergeIds);
  const [otherLinguistIds, setOtherLinguistIds] = useState(initialOtherIds);
  const [isSaving, setIsSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [otherSearch, setOtherSearch] = useState("");

  const filteredOtherLinguistIds = useMemo(() => {
    const query = String(otherSearch || "").trim().toLowerCase();
    if (!query) return otherLinguistIds;

    return otherLinguistIds.filter((linguistId) => {
      const linguist = linguistById.get(Number(linguistId));
      if (!linguist) return false;

      const name = String(linguist.Name || "").toLowerCase();
      const shortName = String(linguist.ShortName || "").toLowerCase();
      const email = String(linguist.Email || "").toLowerCase();
      return (
        name.includes(query) ||
        shortName.includes(query) ||
        email.includes(query)
      );
    });
  }, [otherSearch, otherLinguistIds, linguistById]);

  const keptName = useMemo(() => {
    const keepId = Number(linguistsToMergeIds[0]);
    return linguistById.get(keepId)?.Name || "";
  }, [linguistById, linguistsToMergeIds]);

  const keptShortName = useMemo(() => {
    const keepId = Number(linguistsToMergeIds[0]);
    return linguistById.get(keepId)?.ShortName || "";
  }, [linguistById, linguistsToMergeIds]);

  const moveLinguistToMerge = (linguistId, targetIndex) => {
    const linguistIdNumber = Number(linguistId);
    if (!otherLinguistIds.includes(linguistIdNumber)) return;

    const nextOther = otherLinguistIds.filter((id) => id !== linguistIdNumber);
    const safeIndex =
      typeof targetIndex === "number"
        ? Math.max(0, Math.min(targetIndex, linguistsToMergeIds.length))
        : linguistsToMergeIds.length;

    const nextMerge = [...linguistsToMergeIds];
    nextMerge.splice(safeIndex, 0, linguistIdNumber);

    setLinguistsToMergeIds(nextMerge);
    setOtherLinguistIds(nextOther);
  };

  const sortOtherLinguistIdsByMatch = useCallback(
    (ids, mergeIds) => {
      const keepId = Number((mergeIds || [])[0]);
      const keepLinguist = linguistById.get(keepId);
      const keepName = keepLinguist?.Name || "";
      const keepShortName = keepLinguist?.ShortName || "";

      return [...ids]
        .map((id, index) => {
          const linguist = linguistById.get(Number(id));
          return {
            id,
            index,
            nameScore: getNameMatchPercent(keepName, linguist?.Name),
            shortNameScore: getNameMatchPercent(
              keepShortName,
              linguist?.ShortName || "",
            ),
          };
        })
        .sort((a, b) => {
          if (b.nameScore !== a.nameScore) return b.nameScore - a.nameScore;
          if (b.shortNameScore !== a.shortNameScore) {
            return b.shortNameScore - a.shortNameScore;
          }
          return a.index - b.index;
        })
        .map((item) => item.id);
    },
    [getNameMatchPercent, linguistById],
  );

  const moveLinguistToOther = (linguistId, targetIndex) => {
    const linguistIdNumber = Number(linguistId);
    if (!linguistsToMergeIds.includes(linguistIdNumber)) return;

    const nextMerge = linguistsToMergeIds.filter((id) => id !== linguistIdNumber);
    const nextOther = [...otherLinguistIds];
    if (!nextOther.includes(linguistIdNumber)) {
      nextOther.push(linguistIdNumber);
    }

    const sortedOther = sortOtherLinguistIdsByMatch(nextOther, nextMerge);

    setLinguistsToMergeIds(nextMerge);
    setOtherLinguistIds(sortedOther);
  };

  const handleDragEnd = (result) => {
    setIsDragging(false);

    const { source, destination } = result;
    if (!destination) return;
    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    if (source.droppableId === "merge-linguists") {
      const mergeSource = [...linguistsToMergeIds];
      const [movedId] = mergeSource.splice(source.index, 1);

      if (destination.droppableId === "merge-linguists") {
        mergeSource.splice(destination.index, 0, movedId);
        setLinguistsToMergeIds(mergeSource);
      } else {
        const nextOther = [...otherLinguistIds, movedId];
        const sortedOther = sortOtherLinguistIdsByMatch(nextOther, mergeSource);
        setLinguistsToMergeIds(mergeSource);
        setOtherLinguistIds(sortedOther);
      }
      return;
    }

    const otherSource = [...otherLinguistIds];
    const [movedId] = otherSource.splice(source.index, 1);

    if (destination.droppableId === "other-linguists") {
      otherSource.splice(destination.index, 0, movedId);
      setOtherLinguistIds(otherSource);
    } else {
      const nextMerge = [...linguistsToMergeIds];
      nextMerge.splice(destination.index, 0, movedId);
      setLinguistsToMergeIds(nextMerge);
      setOtherLinguistIds(otherSource);
    }
  };

  const mergeLinguists = async () => {
    if (linguistsToMergeIds.length < 2) {
      showSnackbar(
        "Select at least 2 linguists in 'Linguists to Merge'. The first one is kept.",
        theme,
        "error",
      );
      return;
    }

    const keepLinguistId = Number(linguistsToMergeIds[0]);
    const mergeLinguistIds = linguistsToMergeIds.slice(1).map(Number);

    try {
      setIsSaving(true);

      const response = await api.post("/linguists/mergeLinguists", {
        data: {
          keepLinguistId,
          mergeLinguistIds,
        },
      });

      const movedServiceLinksCount =
        Number(response?.data?.movedServiceLinksCount) || 0;
      const movedServiceHistoryFromLinksCount =
        Number(response?.data?.movedServiceHistoryFromLinksCount) || 0;
      const movedServiceHistoryToLinksCount =
        Number(response?.data?.movedServiceHistoryToLinksCount) || 0;
      const movedLanguageLinksCount =
        Number(response?.data?.movedLanguageLinksCount) || 0;
      const movedRequirementLinksCount =
        Number(response?.data?.movedRequirementLinksCount) || 0;
      const movedAvailabilityLinksCount =
        Number(response?.data?.movedAvailabilityLinksCount) || 0;
      const movedGeneratedDocumentLinksCount =
        Number(response?.data?.movedGeneratedDocumentLinksCount) || 0;
      const movedTaskLinksCount = Number(response?.data?.movedTaskLinksCount) || 0;
      const movedBugReportLinksCount =
        Number(response?.data?.movedBugReportLinksCount) || 0;
      const movedNoteLinksCount = Number(response?.data?.movedNoteLinksCount) || 0;
      const deletedLinguistsCount =
        Number(response?.data?.deletedLinguistsCount) || 0;

      if (socket) {
        socket.emit("entity:saved", {
          modelType: "LinguistsList",
          recordId: "all",
          changeType: "Linguist",
          changeAction: "Merged",
          user: {
            id: currentUser?.id,
            name: currentUser?.Name || "User",
            imageThumbnail: currentUser?.ImageThumbnail,
          },
        });
      }

      triggerPageRefresh(
        `Linguists merged successfully. Deleted ${deletedLinguistsCount} merged linguist(s). Re-linked ${movedServiceLinksCount} service(s), ${movedServiceHistoryFromLinksCount + movedServiceHistoryToLinksCount} service history link(s), ${movedLanguageLinksCount} language link(s), ${movedRequirementLinksCount} requirement value(s), ${movedAvailabilityLinksCount} availability row(s), ${movedGeneratedDocumentLinksCount} generated document(s), ${movedTaskLinksCount} task(s), ${movedBugReportLinksCount} bug report(s), and ${movedNoteLinksCount} note(s).`,
        theme,
      );

      setOverlayName("");
    } catch (error) {
      const statusCode = error?.response?.status;
      const baseMessage =
        error?.response?.data?.error ||
        "An error occurred while merging linguists.";
      const details = error?.response?.data?.details;
      const message = details
        ? `${baseMessage} (${details})`
        : statusCode
          ? `${baseMessage} (HTTP ${statusCode})`
          : baseMessage;
      showSnackbar(message, theme, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const renderLinguistCard = (linguistId, index, listType) => {
    const linguist = linguistById.get(Number(linguistId));
    if (!linguist) return null;

    const isMergeList = listType === "merge";
    const isPrimaryKept = isMergeList && index === 0;

    const backgroundColor = isPrimaryKept
      ? alpha(theme.palette.primary.main, 0.12)
      : isMergeList
        ? alpha(theme.palette.warning.main, 0.12)
        : theme.palette.background.default;

    const borderColor = isPrimaryKept
      ? theme.palette.primary.main
      : isMergeList
        ? theme.palette.warning.main
        : theme.palette.primary.border2;

    const chipLabel = isPrimaryKept ? "Keep" : isMergeList ? "Merge" : "Other";
    const nameMatchPercent = isMergeList
      ? null
      : getNameMatchPercent(keptName, linguist.Name);
    const shortNameMatchPercent = isMergeList
      ? null
      : getNameMatchPercent(keptShortName, linguist.ShortName || "");

    return (
      <Box
        key={`linguist-${linguist.id}`}
        sx={{
          mb: 1,
          p: 1.25,
          borderRadius: "12px",
          bgcolor: backgroundColor,
          border: `1px solid ${borderColor}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 0.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: theme.palette.text.secondary,
                cursor: "grab",
              }}
            >
              <DragIndicatorRoundedIcon fontSize="small" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" fontWeight={700} noWrap>
                {linguist.Name || "Unnamed Linguist"}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {linguist.ShortName || linguist.Email || "No Short Name"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Chip
              label={chipLabel}
              size="small"
              sx={{
                bgcolor: isPrimaryKept
                  ? theme.palette.primary.main
                  : isMergeList
                    ? theme.palette.warning.main
                    : theme.palette.background.alt,
                color:
                  isPrimaryKept || isMergeList
                    ? theme.palette.background.default
                    : theme.palette.text.primary,
                fontWeight: 700,
              }}
            />
            {isMergeList ? (
              <IconButton
                size="small"
                onClick={() => moveLinguistToOther(linguist.id)}
                aria-label="Remove from merge list"
              >
                <RemoveRoundedIcon fontSize="small" />
              </IconButton>
            ) : (
              <IconButton
                size="small"
                onClick={() => moveLinguistToMerge(linguist.id)}
                aria-label="Add to merge list"
              >
                <AddRoundedIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block" }}
        >
          Status: {linguist.Status || "N/A"}
        </Typography>
        {!isMergeList && (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Name Match: {nameMatchPercent}%
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Short Name Match: {shortNameMatchPercent}%
            </Typography>
          </>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        bgcolor: "rgba(0, 0, 0, 0.8)",
        zIndex: 1300,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          bgcolor: theme.palette.background.default,
          width: { xs: "100%", sm: "95%", md: "88%", lg: "80%" },
          minWidth: { xs: "100%", sm: "720px" },
          maxWidth: "1400px",
          height: "90vh",
          maxHeight: "90vh",
          borderRadius: "1.5rem",
          display: "flex",
          flexDirection: "column",
          color: theme.palette.text.primary,
          p: "0rem .2rem 1.5rem 0",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            p: "0.5rem 2rem",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton color="primary" size="large" onClick={() => setOverlayName("")}>
              <ChevronLeftRoundedIcon fontSize="inherit" />
            </IconButton>
            <Box>
              <Typography variant={isMobile ? "h4" : "h2"} fontWeight="bold">
                Merge Linguists
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Drag linguists between lists or use +/- buttons. In the left list,
                the first linguist is kept and the rest are merged into it.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              color="primary"
              variant="outlined"
              size="large"
              endIcon={<DisabledByDefaultRoundedIcon />}
              onClick={() => setOverlayName("")}
              sx={{ borderRadius: "12px" }}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              size="large"
              endIcon={<MergeRoundedIcon />}
              disabled={isSaving || linguistsToMergeIds.length === 0}
              onClick={mergeLinguists}
              sx={{ borderRadius: "12px" }}
            >
              Merge
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            bgcolor: theme.palette.background.alt,
            border: `1px solid ${theme.palette.primary.border2}`,
            borderRadius: "1.5rem",
            display: "flex",
            p: "1rem 1.5rem",
            width: "100%",
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          <DragDropContext onDragStart={() => setIsDragging(true)} onDragEnd={handleDragEnd}>
            <Grid
              container
              spacing={2}
              sx={{ width: "100%", minHeight: 0, height: "100%" }}
            >
            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ minHeight: 0, height: { xs: "auto", md: "100%" }, display: "flex" }}
            >
              <Box
                sx={{
                  flex: 1,
                  height: { xs: "auto", md: "100%" },
                  minHeight: 0,
                  borderRadius: "12px",
                  border: `1px solid ${theme.palette.primary.border2}`,
                  bgcolor: theme.palette.background.default,
                  p: 1.25,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                  Linguists to Merge
                </Typography>
                <Droppable droppableId="merge-linguists" type="LINGUIST">
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        height: { xs: "30vh", md: "100%" },
                        maxHeight: { xs: "30vh", md: "100%" },
                        overflowY: "auto",
                        borderRadius: "12px",
                        p: 0.5,
                        bgcolor:
                          snapshot.isDraggingOver || isDragging
                            ? alpha(theme.palette.primary.main, 0.08)
                            : "transparent",
                      }}
                    >
                      {linguistsToMergeIds.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                          Add at least two linguists here to merge.
                        </Typography>
                      ) : (
                        linguistsToMergeIds.map((linguistId, index) => (
                          <Draggable
                            key={linguistId}
                            draggableId={String(linguistId)}
                            index={index}
                          >
                            {(drag) => (
                              <Box
                                ref={drag.innerRef}
                                {...drag.draggableProps}
                                {...drag.dragHandleProps}
                              >
                                {renderLinguistCard(linguistId, index, "merge")}
                              </Box>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </Box>
            </Grid>

            <Grid
              size={{ xs: 12, md: 6 }}
              sx={{ minHeight: 0, height: { xs: "auto", md: "100%" }, display: "flex" }}
            >
              <Box
                sx={{
                  flex: 1,
                  height: { xs: "auto", md: "100%" },
                  minHeight: 0,
                  borderRadius: "12px",
                  border: `1px solid ${theme.palette.primary.border2}`,
                  bgcolor: theme.palette.background.default,
                  p: 1.25,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                  Other Linguists
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search other linguists"
                  value={otherSearch}
                  onChange={(event) => setOtherSearch(event.target.value)}
                  sx={{ mb: 1 }}
                />
                <Droppable droppableId="other-linguists" type="LINGUIST">
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        height: { xs: "30vh", md: "100%" },
                        maxHeight: { xs: "30vh", md: "100%" },
                        overflowY: "auto",
                        borderRadius: "12px",
                        p: 0.5,
                        bgcolor:
                          snapshot.isDraggingOver || isDragging
                            ? alpha(theme.palette.warning.main, 0.1)
                            : "transparent",
                      }}
                    >
                      {otherLinguistIds.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                          No linguists remaining.
                        </Typography>
                      ) : filteredOtherLinguistIds.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                          No linguists match your search.
                        </Typography>
                      ) : (
                        filteredOtherLinguistIds.map((linguistId, index) => (
                          <Draggable
                            key={linguistId}
                            draggableId={String(linguistId)}
                            index={index}
                          >
                            {(drag) => (
                              <Box
                                ref={drag.innerRef}
                                {...drag.draggableProps}
                                {...drag.dragHandleProps}
                              >
                                {renderLinguistCard(linguistId, index, "other")}
                              </Box>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </Box>
            </Grid>
            </Grid>
          </DragDropContext>
        </Box>
      </Box>
    </Box>
  );
};

export default LinguistMergeOverlay;
